import { useState, useEffect, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Plus, Pencil, Truck, Save, Download, FileText, Wand2, AlertTriangle, ClipboardList, Clock, Search, X, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EmptyState } from "@/components/library/states";
import { upsertIngredient, saveStockCount, setProductStockConfig, productsBySupplier, orderSuggestions, lastStockCount, getLagerReminderDays, setLagerReminderDays, reorderProducts, UNITS } from "@/lib/dataStore";
import { downloadCSV, downloadPDF } from "@/lib/exporters";

const kr = (n) => `${(Number(n) || 0).toLocaleString("sv-SE", { maximumFractionDigits: 2 })} kr`;
const krUnit = (n) => `${(Number(n) || 0).toLocaleString("sv-SE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kr`;
const STOCK_UNITS = ["st", "kg", "l"];
const unitOptions = (u) => (STOCK_UNITS.includes(u) ? STOCK_UNITS : [u, ...STOCK_UNITS]);

// Small reusable search box for filtering product lists.
function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative w-full sm:w-64" data-testid={`${testid}-wrap`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 pl-9 pr-8" data-testid={testid} />
      {value && (
        <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
      )}
    </div>
  );
}

// ---------- Beställning (personal — visar INGA priser) ----------
export function LagerBestallning({ rid, actor }) {
  const groups = productsBySupplier(rid);
  const suppliers = groups.map((g) => g.supplier);
  const [supplier, setSupplier] = useState(suppliers[0] || "");
  const [query, setQuery] = useState("");
  const items = (groups.find((g) => g.supplier === supplier)?.items) || [];
  const q = query.trim().toLowerCase();
  const visibleItems = q ? items.filter((p) => p.name.toLowerCase().includes(q)) : items;
  const [counts, setCounts] = useState({});
  const [extras, setExtras] = useState(Array.from({ length: 5 }, () => ({ name: "", qty: "" })));

  useEffect(() => { if (!supplier && suppliers[0]) setSupplier(suppliers[0]); }, [suppliers.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCount = (id, v) => setCounts((c) => ({ ...c, [id]: v }));
  const setExtra = (i, patch) => setExtras((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const currentOf = (p) => (counts[p.id] !== undefined ? counts[p.id] : (p.current_stock ?? ""));
  const orderOf = (p) => {
    const finns = currentOf(p);
    const cur = finns === "" ? 0 : Number(finns);
    return Math.max((Number(p.par_level) || 0) - cur, 0);
  };
  const totalToOrder = items.reduce((a, p) => a + orderOf(p), 0);
  const underPar = items.filter((p) => (p.par_level || 0) > 0 && orderOf(p) > 0).length;
  const last = lastStockCount(rid);
  const daysSince = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000) : null;
  const reminderDays = getLagerReminderDays(rid);
  const needsCount = daysSince === null || daysSince >= reminderDays;

  const suggest = () => setCounts(Object.fromEntries(items.map((p) => [p.id, String(p.current_stock ?? 0)])));

  const save = () => {
    const toSave = {};
    Object.entries(counts).forEach(([id, v]) => { if (v !== "") toSave[id] = v; });
    if (Object.keys(toSave).length === 0) return toast.error("Ange minst ett antal");
    const n = saveStockCount(rid, toSave, actor);
    setCounts({});
    toast.success(`Inventering sparad — ${n} produkter uppdaterade`);
  };

  const headers = ["Produkt", "Behov", "Finns nu", "Att beställa"];
  const exportRows = () => [
    ...items.map((p) => [p.name, `${p.par_level || 0} ${p.stock_unit || "st"}`, currentOf(p) === "" ? "—" : `${currentOf(p)}`, `${orderOf(p)}`]),
    ...extras.map((e) => [e.name || "", "—", "—", e.qty || ""]),
  ];

  if (suppliers.length === 0) return <EmptyState title="Inga produkter ännu" hint="Produkter läggs till av ledningen i fliken Produkter." />;

  return (
    <div className="space-y-6" data-testid="lager-bestallning">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 w-full sm:w-56">
            <Label>Leverantör</Label>
            <Select value={supplier} onValueChange={(v) => { setSupplier(v); setCounts({}); setQuery(""); }}>
              <SelectTrigger data-testid="bestallning-supplier"><SelectValue /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Sök produkt</Label>
            <SearchBox value={query} onChange={setQuery} placeholder="Sök i listan…" testid="bestallning-search" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OrderSuggestionDialog rid={rid} />
          <Button variant="secondary" size="sm" onClick={suggest} data-testid="bestallning-suggest"><Wand2 className="h-4 w-4 mr-1" />Beställningsförslag</Button>
          <Button variant="secondary" size="sm" onClick={() => downloadCSV(`bestallning_${supplier}.csv`, headers, exportRows())} data-testid="bestallning-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="secondary" size="sm" onClick={() => downloadPDF(`Beställning – ${supplier}`, new Date().toLocaleString("sv-SE"), headers, exportRows())} data-testid="bestallning-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      </div>

      {needsCount && (
        <div className="flex items-center gap-2 text-sm rounded-xl border border-border bg-secondary/40 px-4 py-2.5" data-testid="bestallning-reminder">
          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>{last ? `Senaste inventeringen var ${daysSince} dagar sedan — dags att inventera igen.` : "Ingen inventering gjord ännu — gör din första lagerräkning."}</span>
        </div>
      )}

      {underPar > 0 && (
        <div className="flex items-center gap-2 text-sm rounded-xl border border-[hsl(38_92%_50%/.35)] bg-[hsl(38_92%_50%/.08)] px-4 py-2.5" data-testid="bestallning-underpar">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
          <span><span className="font-semibold text-foreground">{underPar}</span> produkter under par-nivå hos {supplier} — behöver beställas.</span>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-4 py-3 text-xs text-muted-foreground border-b border-border uppercase tracking-wide">
          <span>Produkt</span><span>Behov</span><span>Finns nu</span><span>Att beställa</span>
        </div>
        {visibleItems.map((p) => {
          const need = orderOf(p);
          return (
            <div key={p.id} data-testid={`bestallning-row-${p.id}`} className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm">
              <span className="font-medium">{p.name}</span>
              <span className="text-muted-foreground">{p.par_level || 0} {p.stock_unit || "st"}</span>
              <Input type="number" min="0" inputMode="numeric" value={currentOf(p)} onChange={(e) => setCount(p.id, e.target.value)} placeholder="0" className="h-9 max-w-[120px]" data-testid={`bestallning-count-${p.id}`} />
              <span className={`font-display font-bold ${need > 0 ? "text-warning" : "text-muted-foreground"}`} data-testid={`bestallning-order-${p.id}`}>{need > 0 ? `${need} ${p.stock_unit || "st"}` : "—"}</span>
            </div>
          );
        })}
        {visibleItems.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="bestallning-no-results">Ingen produkt matchar "{query}"</p>}
      </div>

      <div>
        <p className="overline text-muted-foreground mb-2">Övrigt (fyll i vid behov)</p>
        <div className="rounded-2xl border border-dashed border-border bg-card/50 overflow-hidden">
          {extras.map((e, i) => (
            <div key={i} data-testid={`bestallning-extra-${i}`} className="grid grid-cols-[2fr_1fr] gap-2 px-4 py-2 border-b border-border last:border-0 items-center">
              <Input value={e.name} onChange={(ev) => setExtra(i, { name: ev.target.value })} placeholder={`Övrig vara ${i + 1}`} className="h-9" data-testid={`bestallning-extra-name-${i}`} />
              <Input value={e.qty} onChange={(ev) => setExtra(i, { qty: ev.target.value })} placeholder="Antal" className="h-9 max-w-[120px]" data-testid={`bestallning-extra-qty-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="bestallning-total">Totalt att beställa: <span className="text-foreground font-semibold">{totalToOrder}</span></p>
        <Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="bestallning-save"><Save className="h-4 w-4 mr-1" />Spara inventering</Button>
      </div>
    </div>
  );
}

// ---------- Skapa beställning (allt under par, alla leverantörer) ----------
function OrderSuggestionDialog({ rid }) {
  const [open, setOpen] = useState(false);
  const groups = open ? orderSuggestions(rid) : [];
  const total = groups.reduce((a, g) => a + g.items.length, 0);
  const headers = ["Leverantör", "Produkt", "Att beställa"];
  const rows = groups.flatMap((g) => g.items.map((it) => [g.supplier, it.name, `${it.order} ${it.stock_unit}`]));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-accent text-white hover:opacity-90" data-testid="bestallning-create-all"><ClipboardList className="h-4 w-4 mr-1" />Skapa beställning</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Beställningsförslag – allt under par-nivå</DialogTitle></DialogHeader>
        {total === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="order-suggest-empty">Inga produkter under par-nivå – allt är påfyllt 👍</p>
        ) : (
          <div className="space-y-5" data-testid="order-suggest-list">
            {groups.map((g) => (
              <div key={g.supplier}>
                <p className="flex items-center gap-2 text-sm font-display font-semibold mb-2"><Truck className="h-4 w-4 text-muted-foreground" />{g.supplier} <span className="text-muted-foreground font-sans font-normal">· {g.items.length}</span></p>
                <div className="rounded-xl border border-border overflow-hidden">
                  {g.items.map((it) => (
                    <div key={it.id} className="flex items-center justify-between px-3 py-2 text-sm border-b border-border last:border-0">
                      <span>{it.name}</span>
                      <span className="font-display font-bold text-warning">{it.order} {it.stock_unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {total > 0 && (
          <DialogFooter className="gap-2">
            <Button variant="secondary" onClick={() => downloadCSV("bestallning_alla.csv", headers, rows)} data-testid="order-suggest-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
            <Button onClick={() => downloadPDF("Beställning – alla leverantörer", new Date().toLocaleString("sv-SE"), headers, rows)} data-testid="order-suggest-pdf" className="bg-primary text-primary-foreground hover:opacity-90"><FileText className="h-4 w-4 mr-1" />Exportera PDF</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Produkter (ledning — priser + par-nivå + aktuellt lager) ----------
export function LagerProdukter({ rid, actor, canManage }) {
  const [typeFilter, setTypeFilter] = useState("alla");
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const allGroups = productsBySupplier(rid);
  const groups = allGroups
    .map((g) => ({ ...g, items: g.items.filter((p) =>
      (typeFilter === "alla" || (p.product_type || "ravara") === typeFilter) &&
      (!q || p.name.toLowerCase().includes(q) || (p.supplier || "").toLowerCase().includes(q))) }))
    .filter((g) => g.items.length);

  if (allGroups.length === 0) return (
    <div className="space-y-6" data-testid="lager-produkter">
      <div className="flex justify-end">{canManage && <ProductEditor rid={rid} actor={actor} />}</div>
      <EmptyState title="Inga produkter ännu" hint={canManage ? "Lägg till den första produkten med knappen ovan." : undefined} />
    </div>
  );

  const filters = [["alla", "Alla"], ["ravara", "Råvaror"], ["forbrukning", "Förbrukning"]];

  return (
    <div className="space-y-8" data-testid="lager-produkter">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-xl border border-border bg-card p-1" data-testid="produkt-type-filter">
            {filters.map(([v, l]) => (
              <button key={v} onClick={() => setTypeFilter(v)} data-testid={`produkt-filter-${v}`}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${typeFilter === v ? "bg-secondary text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          <SearchBox value={query} onChange={setQuery} placeholder="Sök produkt eller leverantör…" testid="produkt-search" />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {canManage && <ReminderSetting rid={rid} actor={actor} />}
          {canManage && <ProductEditor rid={rid} actor={actor} />}
        </div>
      </div>
      {groups.length === 0 && q && <p className="text-sm text-muted-foreground text-center py-8" data-testid="produkt-no-results">Ingen produkt matchar "{query}"</p>}
      {canManage && groups.length > 0 && (
        <p className="text-xs text-muted-foreground -mt-4">Tips: dra i <GripVertical className="inline h-3.5 w-3.5 -mt-0.5" />-handtaget för att ändra ordning. Ordningen visas även vid beställning.{(q || typeFilter !== "alla") && " (Rensa sök/filter för att kunna dra.)"}</p>
      )}
      {groups.map((g) => (
        <SupplierGroup key={g.supplier} rid={rid} actor={actor} canManage={canManage}
          supplier={g.supplier} items={g.items} draggable={canManage && !q && typeFilter === "alla"} />
      ))}
    </div>
  );
}

function SupplierGroup({ rid, actor, canManage, supplier, items, draggable }) {
  const [order, setOrder] = useState(items);
  const orderRef = useRef(items);
  const idsKey = items.map((i) => i.id).join(",");
  useEffect(() => { setOrder(items); orderRef.current = items; }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReorder = (next) => { setOrder(next); orderRef.current = next; };
  const persist = () => { reorderProducts(rid, orderRef.current.map((p) => p.id), actor); toast.success("Ordning sparad"); };

  return (
    <div>
      <p className="flex items-center gap-2 text-sm font-display font-semibold mb-2"><Truck className="h-4 w-4 text-muted-foreground" />{supplier} <span className="text-muted-foreground font-sans font-normal">· {items.length}</span></p>
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="hidden md:grid grid-cols-[auto_2fr_1fr_1fr_1fr_1.1fr_1fr_auto] gap-2 px-4 py-3 text-xs text-muted-foreground border-b border-border uppercase tracking-wide">
          <span></span><span>Produkt</span><span>Förpackning</span><span>Inköpspris</span><span>Pris/enhet</span><span>Par-nivå</span><span>Aktuellt lager</span><span></span>
        </div>
        <Reorder.Group axis="y" values={order} onReorder={handleReorder} as="div">
          {order.map((p) => (
            <ProductRow key={p.id} p={p} rid={rid} actor={actor} canManage={canManage} draggable={draggable} onPersist={persist} />
          ))}
        </Reorder.Group>
      </div>
    </div>
  );
}

function ProductRow({ p, rid, actor, canManage, draggable, onPersist }) {
  const controls = useDragControls();
  const under = (p.par_level || 0) > 0 && (p.current_stock ?? 0) < p.par_level;
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} onDragEnd={onPersist} as="div"
      data-testid={`produkt-row-${p.id}`}
      className="grid grid-cols-2 md:grid-cols-[auto_2fr_1fr_1fr_1fr_1.1fr_1fr_auto] gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm bg-card">
      <span
        onPointerDown={(e) => draggable && controls.start(e)}
        data-testid={`produkt-drag-${p.id}`}
        className={`hidden md:flex items-center justify-center text-muted-foreground ${draggable ? "cursor-grab active:cursor-grabbing hover:text-foreground touch-none" : "opacity-20 cursor-not-allowed"}`}>
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="font-medium flex items-center gap-2">{p.name}{(p.product_type || "ravara") === "forbrukning" && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground uppercase tracking-wide">Förbr.</span>}</span>
      <span className="text-muted-foreground">{p.pack_size} {p.pack_unit}</span>
      <span>{kr(p.pack_price)}</span>
      <span className="font-mono text-xs">{krUnit(p.price_per_base)}/{p.base_unit}</span>
      <span>{canManage ? <InlinePar rid={rid} actor={actor} p={p} /> : `${p.par_level || 0} ${p.stock_unit || "st"}`}</span>
      <span className={under ? "text-warning font-semibold" : ""} data-testid={`produkt-stock-${p.id}`}>{p.current_stock ?? "—"} {p.current_stock != null ? (p.stock_unit || "st") : ""}{under && <span className="ml-1 text-[10px]">↓ under par</span>}</span>
      <span>{canManage && <ProductEditor rid={rid} actor={actor} existing={p} />}</span>
    </Reorder.Item>
  );
}

function ReminderSetting({ rid, actor }) {
  const [days, setDays] = useState(getLagerReminderDays(rid));
  const save = () => { const v = Math.max(1, Number(days) || 7); if (v !== getLagerReminderDays(rid)) { setLagerReminderDays(rid, v, actor); toast.success(`Påminnelse var ${v} dag(ar)`); } };
  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="reminder-setting">
      <Clock className="h-4 w-4" />
      <span className="hidden sm:inline">Påminn var</span>
      <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} onBlur={save} className="h-8 w-16" data-testid="reminder-days-input" />
      <span>dag(ar)</span>
    </div>
  );
}

function InlinePar({ rid, actor, p }) {
  const [par, setPar] = useState(p.par_level ?? 0);
  const [unit, setUnit] = useState(p.stock_unit || "st");
  const savePar = () => { if (String(par) !== String(p.par_level ?? 0)) setProductStockConfig(rid, p.id, { par_level: par }, actor); };
  const saveUnit = (v) => { setUnit(v); if (v !== (p.stock_unit || "st")) setProductStockConfig(rid, p.id, { stock_unit: v }, actor); };
  return (
    <span className="flex items-center gap-1">
      <Input type="number" min="0" value={par} onChange={(e) => setPar(e.target.value)} onBlur={savePar} className="h-8 w-16" data-testid={`par-input-${p.id}`} />
      <Select value={unit} onValueChange={saveUnit}>
        <SelectTrigger className="h-8 w-[68px] px-2" data-testid={`unit-input-${p.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{unitOptions(unit).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
      </Select>
    </span>
  );
}

function ProductEditor({ rid, actor, existing }) {
  const [open, setOpen] = useState(false);
  const seed = () => existing
    ? { name: existing.name, supplier: existing.supplier || "", pack_size: existing.pack_size, pack_unit: existing.pack_unit || "kg", pack_price: existing.pack_price, par_level: existing.par_level || 0, stock_unit: existing.stock_unit || "st", product_type: existing.product_type || "ravara" }
    : { name: "", supplier: "", pack_size: "", pack_unit: "kg", pack_price: "", par_level: "", stock_unit: "st", product_type: "ravara" };
  const [f, setF] = useState(seed);
  const save = () => {
    if (!f.name.trim()) return toast.error("Ange produktnamn");
    if (!f.pack_size || !f.pack_price) return toast.error("Ange förpackning och inköpspris");
    upsertIngredient(rid, { ...(existing ? { id: existing.id } : {}), name: f.name, supplier: f.supplier, pack_size: f.pack_size, pack_unit: f.pack_unit, pack_price: f.pack_price, par_level: f.par_level, stock_unit: f.stock_unit, product_type: f.product_type }, actor);
    toast.success(existing ? "Produkt uppdaterad" : "Produkt tillagd");
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(seed()); }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`produkt-edit-${existing.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"><Pencil className="h-3.5 w-3.5" /> Redigera</button>
          : <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-produkt"><Plus className="h-4 w-4 mr-1" />Ny produkt</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Redigera produkt" : "Ny produkt"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Produktnamn</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="produkt-name" /></div>
          <div className="space-y-1.5"><Label>Leverantör</Label><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} placeholder="T.ex. Nordisk Ost AB" data-testid="produkt-supplier" /></div>
          <div className="space-y-1.5"><Label>Typ</Label>
            <Select value={f.product_type} onValueChange={(v) => setF({ ...f, product_type: v })}><SelectTrigger data-testid="produkt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ravara">Råvara (ingår i receptkalkylen)</SelectItem>
                <SelectItem value="forbrukning">Förbrukning (städ, förpackning m.m.)</SelectItem>
              </SelectContent></Select></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Förp. storlek</Label><Input type="number" value={f.pack_size} onChange={(e) => setF({ ...f, pack_size: e.target.value })} data-testid="produkt-packsize" /></div>
            <div className="space-y-1.5"><Label>Enhet</Label>
              <Select value={f.pack_unit} onValueChange={(v) => setF({ ...f, pack_unit: v })}><SelectTrigger data-testid="produkt-packunit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Inköpspris (kr)</Label><Input type="number" value={f.pack_price} onChange={(e) => setF({ ...f, pack_price: e.target.value })} data-testid="produkt-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Par-nivå (behov)</Label><Input type="number" value={f.par_level} onChange={(e) => setF({ ...f, par_level: e.target.value })} placeholder="T.ex. 15" data-testid="produkt-parlevel" /></div>
            <div className="space-y-1.5"><Label>Lagerenhet</Label>
              <Select value={f.stock_unit} onValueChange={(v) => setF({ ...f, stock_unit: v })}><SelectTrigger data-testid="produkt-stockunit"><SelectValue /></SelectTrigger>
                <SelectContent>{unitOptions(f.stock_unit).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-produkt">{existing ? "Spara ändringar" : "Lägg till"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
